import type { RecommendationLabMetricRelativeStatus } from '@/models/RecommendationLabReport';
import type {
  SkinAnalysisPredictValidationReport,
  SkinAnalysisPredictValidationReportMetric,
} from '@/models/SkinAnalysisPredictValidation';

/** Code-fixed relative-status wording; the copy prompts can never rename these. */
const STATUS_LABELS: Record<RecommendationLabMetricRelativeStatus, string> = {
  focus_area: 'Focus area',
  on_par: 'On-par',
  ahead: 'Ahead',
};

interface ValidationReportSectionProps {
  report: SkinAnalysisPredictValidationReport;
}

function MetricCard({ metric }: { metric: SkinAnalysisPredictValidationReportMetric }) {
  const status = metric.comparison?.relativeStatus;
  const benchmarkPosition = Math.min(100, Math.max(0, metric.comparison?.benchmarkScore ?? 0));
  const scorePosition = Math.min(100, Math.max(0, metric.score));
  const hasDeepDive = Boolean(metric.cause || metric.action || metric.keyIngredients.length || metric.habits.length);
  return (
    <article className="recommendation-metric-card">
      <div className="recommendation-metric-card-top">
        <div>
          <span className={`recommendation-relative-status recommendation-relative-status-${status ?? 'unknown'}`}>
            {status ? STATUS_LABELS[status] : 'Comparison unavailable'}
          </span>
          <h3>{metric.label}</h3>
        </div>
        <div className="recommendation-metric-card-visual">
          <strong>
            {metric.score}
            <small>/100</small>
          </strong>
        </div>
      </div>
      <div
        className="recommendation-benchmark-track"
        role="img"
        aria-label={`${metric.label} score ${metric.score} out of 100`}
      >
        <span className="recommendation-benchmark-fill" style={{ width: `${scorePosition}%` }} />
        {metric.comparison && (
          <span
            className="recommendation-benchmark-marker"
            style={{ left: `${benchmarkPosition}%` }}
            title={`${metric.comparison.ageBandLabel} average ${metric.comparison.benchmarkScore}`}
          />
        )}
      </div>
      {metric.comparison && (
        <div className="recommendation-benchmark-caption">
          <span>
            {metric.comparison.ageBandLabel} average {metric.comparison.benchmarkScore}
          </span>
          <strong>
            {metric.comparison.deltaPoints >= 0 ? '+' : ''}
            {metric.comparison.deltaPoints} pts
          </strong>
        </div>
      )}
      <h4>{metric.statusHeadline}</h4>
      {metric.benchmarkNarrative && <p>{metric.benchmarkNarrative}</p>}
      {hasDeepDive && (
        <details className="recommendation-metric-details">
          <summary>Read the full {metric.label.toLowerCase()} analysis</summary>
          <div>
            {metric.cause && (
              <section>
                <strong>{metric.cause.title}</strong>
                <p>{metric.cause.body}</p>
              </section>
            )}
            {metric.action && (
              <section>
                <strong>{metric.action.title}</strong>
                <p>{metric.action.body}</p>
              </section>
            )}
            {metric.keyIngredients.length > 0 && (
              <section>
                <strong>{metric.keyIngredientsTitle ?? 'Key ingredients'}</strong>
                <div className="recommendation-tag-list">
                  {metric.keyIngredients.map((ingredient) => (
                    <span key={ingredient.ingredientId}>{ingredient.name}</span>
                  ))}
                </div>
              </section>
            )}
            {metric.habits.length > 0 && (
              <section>
                <strong>Habits and lifestyle</strong>
                <ul>
                  {metric.habits.map((habit) => (
                    <li key={habit}>{habit}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </details>
      )}
    </article>
  );
}

export function ValidationReportSection({ report }: ValidationReportSectionProps) {
  return (
    <section className="recommendation-client-section">
      <div className="recommendation-client-section-heading">
        <div>
          <span className="recommendation-eyebrow">Your skin report</span>
          <h2>Compared with your age benchmark</h2>
        </div>
        <p>
          {report.ageBand
            ? `The grey marker is the ${report.ageBand.label} average. Open a metric for its customer-facing detail.`
            : 'No age band was captured, so the age-band average is not shown for these metrics.'}
        </p>
      </div>
      <div className="recommendation-metric-grid">
        {report.metrics.map((metric) => (
          <MetricCard key={metric.metricKey} metric={metric} />
        ))}
      </div>
    </section>
  );
}
